import { useCallback } from 'react';
import { useMutation, useApolloClient } from '@apollo/client/react';
import type { ApolloClient } from '@apollo/client';
import RefreshTokenService from 'services/RefreshTokenService';
import { useAppContext } from 'contexts/AppContext';
import { LogoutDocument, MeDocument } from 'graphql/generated/graphql';

/**
 * Apollo Client 4 aborts the refetch of a query whose component unmounts during the reset (InitTwoFA after its
 * cancel), Apollo Client 2 let it finish; that abort is no failure of the reset.
 */
const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

export const afterLogout = (client: ApolloClient) => {
  RefreshTokenService.clearRefreshToken();
  client.writeQuery({ query: MeDocument, data: { me: null } });
  // Bug: `.catch()` without a handler does not catch, so a failed refetch of the reset store is an unhandled
  // rejection; kept as it was (rethrown), except for the abort above.
  client.resetStore().catch((error: unknown) => {
    if (!isAbortError(error)) throw error;
  });
};

// The logout is not awaited by its callers, hence `() => void`.
const useLogout = (): (() => void) => {
  const client = useApolloClient();
  const [logoutMutation] = useMutation(LogoutDocument);
  const { setShowSidebar } = useAppContext();

  const logout = useCallback(async () => {
    await logoutMutation();
    afterLogout(client);
    // The sidebar starts as after a page load at the next login (a mobile sidebar open at the logout is closed).
    setShowSidebar?.('responsive');
  }, [client, logoutMutation, setShowSidebar]);

  return logout;
};

export default useLogout;
